import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { useDraggable } from '@dnd-kit/core';
import { TutorLibraryTopic, searchLibrary } from '../../../services/tutorStructureService';

interface LibraryPanelProps {
  courseId: number;
}

interface DraggableTopicItemProps {
  topic: TutorLibraryTopic;
}

function DraggableTopicItem({ topic }: DraggableTopicItemProps) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `library-${topic.topicId}`,
    data: { type: 'LIBRARY_TOPIC', topicId: topic.topicId, title: topic.title },
  });

  return (
    <ListItem
      ref={setNodeRef}
      dense
      sx={{
        cursor: 'grab',
        opacity: isDragging ? 0.4 : 1,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        mb: 0.5,
        '&:hover': { bgcolor: 'action.hover' },
      }}
      {...listeners}
      {...attributes}
    >
      <DragIndicatorIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 18 }} />
      <ListItemText
        primary={topic.title}
        secondary={topic.description ? topic.description.slice(0, 80) + (topic.description.length > 80 ? '…' : '') : null}
        primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
        secondaryTypographyProps={{ variant: 'caption' }}
      />
    </ListItem>
  );
}

export default function LibraryPanel({ courseId }: LibraryPanelProps) {
  const [query, setQuery] = useState('');
  const [topics, setTopics] = useState<TutorLibraryTopic[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const doSearch = useCallback(
    async (q: string) => {
      if (!q.trim()) {
        setTopics([]);
        setError(null);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const results = await searchLibrary(courseId, q);
        setTopics(results);
      } catch (err: any) {
        setError('Could not load topic library — please try again');
        setTopics([]);
      } finally {
        setLoading(false);
      }
    },
    [courseId]
  );

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(query), 500);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, doSearch]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', gap: 1 }}>
      <Typography variant="subtitle1" fontWeight={600}>
        Topic Library
      </Typography>
      <TextField
        size="small"
        fullWidth
        placeholder="Search topics…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
      />
      <Divider />

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 2 }}>
          <CircularProgress size={24} />
        </Box>
      )}

      {error && (
        <Alert severity="error" action={<Button size="small" onClick={() => doSearch(query)}>Retry</Button>}>
          {error}
        </Alert>
      )}

      {!loading && !error && query.trim() && topics.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', pt: 2 }}>
          No matching topics found — try different keywords or add a custom topic.
        </Typography>
      )}

      {!loading && !error && !query.trim() && (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', pt: 2 }}>
          Type to search the curriculum topic library.
        </Typography>
      )}

      {!loading && !error && topics.length > 0 && (
        <List dense disablePadding sx={{ overflowY: 'auto', flex: 1 }}>
          {topics.map((topic) => (
            <DraggableTopicItem key={topic.topicId} topic={topic} />
          ))}
        </List>
      )}
    </Box>
  );
}
